import { ConditionSettings } from "./models/ConditionSettings";
import { FACT_NAMES_NUMERIC } from "./models/FactNamesNumeric";
import { FACT_NAMES_BOOLEAN } from "./models/FactNamesBoolean";
import { FACT_NAMES_STRING } from "./models/FactNamesString";
import { FACT_NAMES_DATE } from "./models/FactNamesDate";
import { ConditionSettingsNumeric } from "./models/ConditionSettingsNumeric";
import { ConditionSettingsBoolean } from "./models/ConditionSettingsBoolean";
import { ConditionSettingsString } from "./models/ConditionSettingsString";
import { ConditionSettingsDate } from "./models/ConditionSettingsDate";
import { RuleConditions } from "./models/RuleConditions";
import { NUMERIC_OPERATORS } from "./models/NumericOperators";
import { BOOLEAN_OPERATORS } from "./models/BooleanOperators";
import { STRING_OPERATORS } from "./models/StringOperators";
import { ARRAY_OPERATORS } from "./models/ArrayOperators";

export class Conditions {
  /**
   * Transforma un objeto de reglas externas al formato interno de condiciones
   * @param externalRules Objeto con reglas externas (key: nombre, value: datos)
   * @returns Array de condiciones en formato { fact, score, value, operator }
   */
  public static transformRuleObject(externalRules: Record<string, any>): {
    conditions: { all: Array<any> };
  } {
    const result: Array<any> = [];
    Object.values(externalRules).forEach((rule: any) => {
      let value = rule.value;
      switch (rule.type) {
        case "currency":
        case "number":
          const maxValue = Number(value.number.max);
          const minValue = Number(value.number.min);
          if (minValue || minValue === 0) {
            result.push({
              fact: rule.name,
              score: rule.score,
              value: minValue,
              operator: NUMERIC_OPERATORS.greaterThanInclusive,
            });
          }

          if (maxValue || maxValue === 0) {
            result.push({
              fact: rule.name,
              score: rule.score,
              value: maxValue,
              operator: NUMERIC_OPERATORS.lessThanInclusive,
            });
          }

          break;
        case "boolean":
          value = value === "true" || value === true;
          result.push({
            fact: rule.name,
            score: rule.score,
            value,
            operator: "equal",
          });
          break;
        default:
          result.push({
            fact: rule.name,
            score: rule.score,
            value,
            operator: "equal",
          });
          break;
      }
    });
    return { conditions: { all: result } };
  }
  private conditions: (
    | ConditionSettingsNumeric
    | ConditionSettingsBoolean
    | ConditionSettingsString
    | ConditionSettingsDate
  )[] = [];

  /**
   * Adds a condition to the conditions array
   * @param conditionSettings {
   *   fact: FACT_NAMES_NUMERIC,
   *   minValue: null | number,
   *   maxValue: null | number,
   * } | {
   *   fact: FACT_NAMES_BOOLEAN,
   *   value: boolean,
   * }
   * @returns Index of the condition in the array. Call removeCondition(index) to remove it, or updateCondition(index) to overwrite it
   */
  public addCondition(
    conditionSettings:
      | ConditionSettingsNumeric
      | ConditionSettingsBoolean
      | ConditionSettingsString
      | ConditionSettingsDate
  ): number {
    const index = this.conditions.push(conditionSettings);
    return index - 1;
  }

  /**
   * Remove a condition at the specified index
   * @param indexToRemove number: remove condition at this index
   * @returns boolean
   */
  public removeCondition(indexToRemove: number): boolean {
    if (this.conditions[indexToRemove]) {
      this.conditions.splice(indexToRemove, 1);
      return true;
    }
    return false;
  }

  /**
   * Overwrites the condition at the specified index
   * @param conditionSettings New conditions
   * @param indexToUpdate number: update condition at this index
   * @returns boolean
   */
  public updateCondition(
    conditionSettings:
      | ConditionSettingsNumeric
      | ConditionSettingsBoolean
      | ConditionSettingsString
      | ConditionSettingsDate,
    indexToUpdate: number
  ): boolean {
    if (this.conditions[indexToUpdate]) {
      this.conditions[indexToUpdate] = conditionSettings;
      return true;
    }
    return false;
  }

  /**
   * Returns object ready to be used by JSON Rules Engine
   * @returns { Conditions: all: [] }
   */
  public getConditions(): RuleConditions {
    return {
      formRules: this.conditions,
      jsonRules: {
        conditions: {
          all: this.getJsonRulesConditions(),
        },
      },
    };
  }

  private getJsonRulesConditions(): ConditionSettings[] {
    const conditionSettings: ConditionSettings[] = [];

    this.conditions.forEach((c) => {
      if (
        Object.values(FACT_NAMES_NUMERIC)
          .map((fn) => fn.toString())
          .includes(c.fact.toString())
      ) {
        // Numeric fact
        const formFactData = c as ConditionSettingsNumeric;

        if (formFactData.minValue || formFactData.minValue === 0) {
          conditionSettings.push({
            fact: formFactData.fact,
            operator: NUMERIC_OPERATORS.greaterThanInclusive,
            value: formFactData.minValue,
            score: formFactData.score,
          });
        }

        if (formFactData.maxValue || formFactData.maxValue === 0) {
          conditionSettings.push({
            fact: formFactData.fact,
            operator: NUMERIC_OPERATORS.lessThanInclusive,
            value: formFactData.maxValue,
            score: formFactData.score,
          });
        }
      } else if (
        Object.values(FACT_NAMES_BOOLEAN)
          .map((fn) => fn.toString())
          .includes(c.fact.toString())
      ) {
        // Boolean fact
        const formFactData = c as ConditionSettingsBoolean;

        conditionSettings.push({
          fact: formFactData.fact,
          operator: BOOLEAN_OPERATORS.equal,
          value: formFactData.value ? "true" : "false",
          score: formFactData.score,
        });
      } else if (
        Object.values(FACT_NAMES_STRING)
          .map((fn) => fn.toString())
          .includes(c.fact.toString())
      ) {
        // String fact
        const formFactData = c as ConditionSettingsString;

        if (formFactData.exactMatch) {
          if (typeof formFactData.exactMatch === "string") {
            conditionSettings.push({
              fact: formFactData.fact,
              operator: STRING_OPERATORS.equal,
              value: formFactData.exactMatch,
              score: formFactData.score,
            });
          } else {
            conditionSettings.push({
              fact: formFactData.fact,
              operator: ARRAY_OPERATORS.in,
              value: formFactData.exactMatch,
              score: formFactData.score,
            });
          }
        }

        if (formFactData.length) {
          conditionSettings.push({
            fact: formFactData.fact,
            operator: STRING_OPERATORS.length,
            value: formFactData.length,
            score: formFactData.score,
          });
        }
      } else if (
        Object.values(FACT_NAMES_DATE)
          .map((fn) => fn.toString())
          .includes(c.fact.toString())
      ) {
        // Date fact
        const formFactData = c as ConditionSettingsDate;

        if (formFactData.minValue) {
          conditionSettings.push({
            fact: formFactData.fact,
            operator: NUMERIC_OPERATORS.greaterThanInclusive,
            value: formFactData.minValue.getTime(),
            score: formFactData.score,
          });
        }

        if (formFactData.maxValue) {
          conditionSettings.push({
            fact: formFactData.fact,
            operator: NUMERIC_OPERATORS.lessThanInclusive,
            value: formFactData.maxValue.getTime(),
            score: formFactData.score,
          });
        }
      }
    });

    return conditionSettings;
  }
}
